import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './TimeSheet.css';
import { hours } from '../lib/hours';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Shared, because three local copies is how one of them came to render
// ten hours as 1h. See lib/hours.js.
const hrs = hours;
const today = () => new Date().toISOString().slice(0, 10);
const day = (v) => String(v || '').slice(0, 10);

const EMPTY = { hours_logged: '', date_worked: today(), description: '' };

/**
 * A contractor's time on one job - listed, added to, corrected and removed.
 *
 * The same component serves both places a contractor meets their work: the
 * ticket's "Work & costs" tab, and the work order on the portal's "Work &
 * time" tab. It is one
 * component rather than two because the rows underneath are one set of rows;
 * building it twice is how the two screens would start disagreeing about how
 * many hours a job took, which is the thing this feature exists to prevent.
 *
 * Give it a ticketId or a workOrderId. The ticket form is the fuller one - it
 * gets totals and the lock reason from the server in the same response - and
 * the work-order form reads the same entries by their work order and takes
 * its totals from the parent, which already has them.
 *
 * Editing happens in place: a row turns into its own fields rather than
 * opening a dialogue, because correcting "20" to "2" should not cost a
 * contractor more than three keystrokes and a click.
 */
export default function TimeSheet({
  ticketId, workOrderId, token, loggedHours, allocatedHours, onChanged, emptyNote
}) {
  const [sheet, setSheet] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [editing, setEditing] = useState(EMPTY);
  const [confirmId, setConfirmId] = useState(null);

  // Built fresh each render rather than memoised: an object literal in a
  // dependency array changes identity every time and re-runs the effect
  // forever. The eslint disable below is the honest version of that.
  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (ticketId) {
        const res = await axios.get(`${API_BASE}/tickets/${ticketId}/time`, auth);
        const body = res.data;
        // Both guards are load-bearing, and the second one white-screened the
        // whole app. If this route ever answers with an ARRAY - an older
        // deploy, a route that degrades to [], a proxy answering for it -
        // then `body?.entries` is Array.prototype.entries, which is a
        // function. Handing a function to a state setter makes React treat it
        // as an updater and call it, so it throws out of render and React
        // unmounts the tree: a blank page, no message, nothing in the UI to
        // say what happened. `|| []` cannot catch that, because a function is
        // truthy. Only accept the shapes this component can actually read.
        setSheet(body && !Array.isArray(body) ? body : null);
        setEntries(Array.isArray(body?.entries) ? body.entries : []);
      } else {
        const res = await axios.get(`${API_BASE}/work-orders/${workOrderId}/hours`, auth);
        const rows = Array.isArray(res.data) ? res.data : [];
        setEntries(rows);
        setSheet({
          logged_hours: rows.reduce((s, r) => s + Number(r.hours_logged || 0), 0),
          allocated_hours: allocatedHours,
          locked: Boolean(rows[0]?.locked),
          locked_reason: res.headers?.['x-time-locked'] || null,
          writable: true
        });
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load the time on this job');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, workOrderId, token, allocatedHours]);

  useEffect(() => { load(); }, [load]);

  const after = async (message) => {
    setNotice(message);
    setError(null);
    await load();
    // The parent owns the totals shown outside this component - the progress
    // bar on a work order, the hours on a ticket card. Tell it to refetch
    // rather than trying to patch its state from in here.
    if (onChanged) onChanged();
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const body = {
        hours_logged: adding.hours_logged,
        date_worked: adding.date_worked,
        description: adding.description || null
      };
      const res = ticketId
        ? await axios.post(`${API_BASE}/tickets/${ticketId}/time`, body, auth)
        : await axios.post(`${API_BASE}/work-orders/${workOrderId}/hours`, body, auth);
      setAdding(EMPTY);
      await after(res.data?.createdWorkOrder
        ? `${hrs(body.hours_logged)} logged. A work order was raised for this ticket so the time has somewhere to sit.`
        : `${hrs(body.hours_logged)} logged.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record that time');
    } finally { setBusy(false); }
  };

  const startEdit = (entry) => {
    setConfirmId(null);
    setEditingId(entry.id);
    setEditing({
      hours_logged: String(Number(entry.hours_logged)),
      date_worked: day(entry.date_worked),
      description: entry.description || ''
    });
  };

  const submitEdit = async (e, entry) => {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      await axios.put(`${API_BASE}/time/${entry.id}`, {
        hours_logged: editing.hours_logged,
        date_worked: editing.date_worked,
        description: editing.description || null
      }, auth);
      setEditingId(null);
      await after('Time entry updated.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change that entry');
    } finally { setBusy(false); }
  };

  const remove = async (entry) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await axios.delete(`${API_BASE}/time/${entry.id}`, auth);
      setConfirmId(null);
      await after(`${hrs(entry.hours_logged)} removed.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove that entry');
    } finally { setBusy(false); }
  };

  if (loading) return <p className="ts-quiet">Loading time&hellip;</p>;

  // Two different reasons the form can be absent, kept apart on purpose.
  // `locked` is a rule - this ticket is invoiced, or its work order is closed -
  // and a contractor can act on it. `writable === false` is a fault in the
  // deployment. Treating the second as the first is what left a ticket showing
  // no time and no way to add any, with nothing on screen saying why.
  const locked = Boolean(sheet?.locked);
  const broken = sheet?.writable === false;
  const logged = Number(sheet?.logged_hours ?? loggedHours ?? 0);
  const allocated = Number(sheet?.allocated_hours ?? allocatedHours ?? 0);
  const over = allocated > 0 && logged > allocated;

  return (
    <div className="ts">
      <div className="ts-summary">
        <span className={over ? 'ts-total is-over' : 'ts-total'}>
          {hrs(logged)}{allocated > 0 ? <span className="ts-of"> of {hrs(allocated)}</span> : null}
        </span>
        <span className="ts-count">
          {entries.length === 0
            ? 'no sessions yet'
            : `${entries.length} session${entries.length === 1 ? '' : 's'}`}
        </span>
        {over && <span className="ts-flag">over the allocation</span>}
        {locked && (
          <span className="ts-lock" title="Locked">
            {sheet?.locked_reason || 'Locked'}
          </span>
        )}
        {broken && <span className="ts-flag">not available</span>}
      </div>

      {error && <p className="ts-error" role="alert">{error}</p>}
      {notice && <p className="ts-ok" role="status">{notice}</p>}

      {entries.length === 0 ? (
        <p className="ts-quiet">
          {emptyNote || 'No time logged against this job yet.'}
        </p>
      ) : (
        <div className="table-scroll">
        <table className="ts-table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col" className="ts-num">Hours</th>
              <th scope="col">What you did</th>
              <th scope="col" className="ts-actions-head">
                <span className="ts-sr">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => {
              if (editingId === entry.id) {
                return (
                  <tr key={entry.id} className="ts-row is-editing">
                    <td colSpan={4}>
                      <form className="ts-form" onSubmit={(e) => submitEdit(e, entry)}>
                        <div className="ts-field">
                          <label htmlFor={`ed-${entry.id}`}>Date</label>
                          <input id={`ed-${entry.id}`} type="date" value={editing.date_worked}
                                 onChange={(e) => setEditing({ ...editing, date_worked: e.target.value })} />
                        </div>
                        <div className="ts-field ts-narrow">
                          <label htmlFor={`eh-${entry.id}`}>Hours</label>
                          <input id={`eh-${entry.id}`} type="number" min="0.25" step="0.25"
                                 value={editing.hours_logged}
                                 onChange={(e) => setEditing({ ...editing, hours_logged: e.target.value })} />
                        </div>
                        <div className="ts-field ts-grow">
                          <label htmlFor={`en-${entry.id}`}>What you did</label>
                          <input id={`en-${entry.id}`} type="text" value={editing.description}
                                 onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                        </div>
                        <div className="ts-buttons">
                          <button type="submit" className="ts-btn is-primary" disabled={busy}>
                            {busy ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" className="ts-btn" onClick={() => setEditingId(null)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={entry.id} className="ts-row">
                  <td>{day(entry.date_worked)}</td>
                  <td className="ts-num">{hrs(entry.hours_logged)}</td>
                  <td>
                    {entry.description || <span className="ts-quiet-inline">&mdash;</span>}
                    {/* Both columns default to the transaction's own
                        timestamp, so a new entry has them exactly equal and
                        any later change makes updated_at strictly greater.
                        Strict inequality is exact; a millisecond tolerance
                        would be a guess. */}
                    {entry.updated_at && entry.logged_at
                      && new Date(entry.updated_at) > new Date(entry.logged_at) && (
                      <span className="ts-amended" title={`Amended ${new Date(entry.updated_at).toLocaleString('en-GB', { timeZone: 'Europe/Malta' })}`}>
                        amended
                      </span>
                    )}
                  </td>
                  <td className="ts-actions">
                    {locked || broken ? (
                      <span className="ts-quiet-inline">{locked ? 'locked' : '\u2014'}</span>
                    ) : confirmId === entry.id ? (
                      <>
                        <button type="button" className="ts-btn is-danger" disabled={busy}
                                onClick={() => remove(entry)}>
                          {busy ? 'Removing…' : 'Remove it'}
                        </button>
                        <button type="button" className="ts-btn" onClick={() => setConfirmId(null)}>
                          Keep
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="ts-btn" onClick={() => startEdit(entry)}>
                          Edit
                        </button>
                        <button type="button" className="ts-btn" onClick={() => setConfirmId(entry.id)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}

      {broken ? (
        <p className="ts-error" role="alert">
          {sheet?.not_writable_reason
            || 'Time cannot be recorded on this ticket at the moment.'}
        </p>
      ) : locked ? (
        <p className="ts-quiet">
          {sheet?.invoice_number
            ? `This work is on invoice ${sheet.invoice_number}, so its hours are fixed. `
              + 'If a figure is wrong, raise it with NCMB rather than editing it here.'
            : 'This job is closed to further time.'}
        </p>
      ) : (
        <form className="ts-form ts-add" onSubmit={submitAdd}>
          <div className="ts-field">
            <label htmlFor={`ad-${ticketId || workOrderId}`}>Date worked</label>
            <input id={`ad-${ticketId || workOrderId}`} type="date" value={adding.date_worked}
                   onChange={(e) => setAdding({ ...adding, date_worked: e.target.value })} />
          </div>
          <div className="ts-field ts-narrow">
            <label htmlFor={`ah-${ticketId || workOrderId}`}>Hours</label>
            <input id={`ah-${ticketId || workOrderId}`} type="number" min="0.25" step="0.25"
                   placeholder="2" value={adding.hours_logged}
                   onChange={(e) => setAdding({ ...adding, hours_logged: e.target.value })} />
          </div>
          <div className="ts-field ts-grow">
            <label htmlFor={`an-${ticketId || workOrderId}`}>What you did (optional)</label>
            <input id={`an-${ticketId || workOrderId}`} type="text" placeholder="Replaced the switch"
                   value={adding.description}
                   onChange={(e) => setAdding({ ...adding, description: e.target.value })} />
          </div>
          <div className="ts-buttons">
            <button type="submit" className="ts-btn is-primary" disabled={busy}>
              {busy ? 'Adding…' : 'Add time'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
